/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 10/10/2017
 * Upgraded: 12/10/2021
 */
import * as _ from 'underscore';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { IUserData } from '../../shared/services/auth/auth-data.service';
import { ICompany, IDescribedObject, ILanguage } from '../../../type_definitions/datatypes';
import { AuthService } from '../../shared/services/auth/auth.service';

import { USERPROFILE_WIZARD, UserProfileService } from './user-profile.service';
import { IUserProfileRequest, UserProfileDataService } from './userprofile-data.service';
import { BodyClassService } from '../../shared/services/body-class.service';
import { UserDataService } from '../../shared/user/user-data.service';
import { GA1Service, MESSAGE_USERPROFILE_SAVED } from '../../shared/ga1/ga1.service';
import { GaEventService } from '../../shared/services/ga-event.service';
import { DialogSize, GaMessageService, IDialog } from '../../shared/message/ga-message.service';
import { STATE_HOME_PAGE, STATE_USER_AUDIT, STATE_USER_INFO, STATE_USER_NOTIFICATIONS, STATE_USER_PERMISSIONS, STATE_USER_TRAINING } from '../../shared/app-states';
import { TranslationService } from '../../shared/translation/translation.service';
import { StateService, Transition, UiOnParamsChanged } from '@uirouter/angular';
import { UserInfoComponent } from './user-info/user-info.component';
import { UserPermissionsComponent } from './permissions/user-permissions.component';
import { DefaultDialogComponent } from '../../shared/message/dialogs/default-dialog.component';
import { EVENT_USER_PROFILE_UPDATED } from '../../shared/app-events';
import { GaWizardService } from '../../shared/wizard/ga-wizard.service';
import { GaWizard, IWizardButton, IWizardStep } from '../../shared/wizard/ga-wizard';
import { UserNotificationsComponent } from './notifications/user-notifications.component';
import { UserAuditComponent } from './audit/user-audit.component';
import { ITranslateText } from '../../shared/wizard/wizard-wrapper.component';
import { UserTrainingComponent } from './training/user-training.component';
import { UserProfileStepIds } from './user-profile-steps';

const SAVE_ACTION:string = 'save';
const CREATE_ACTION:string = 'create';
const APPROVE_ACTION:string = 'approve';
const REJECT_ACTION:string = 'reject';
const SEND_PROFILE_ACTION:string = 'sendprofile';
export const CANCEL_ACTION:string = 'cancel';

// if this changes, the top and/or menu UI need to be reloaded
interface IUserProfileUIData {
  firstname:string;
  lastname:string;
  email:string;
  language:ILanguage;
  gaLayout:IDescribedObject;
}

@Component({
  selector: 'user-profile',
  template: `
    <wizard-wrapper [wizardTitle]="title" [loading]="loading" [wizardId]="wizardId">
      <div class="wizard-actions-before d-inline-block">
        <span class="form-inline" *ngIf="showSendMail">
          <mail-notifications [value]="sendMail" (onChange)="onMailNotificationChange($event)"></mail-notifications>
        </span>
      </div>
      <div class="wizard-actions-after d-inline-block">
        <ng-content></ng-content>
      </div>
    </wizard-wrapper>
  `,
  styleUrls: ['./user-profile.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserProfileComponent implements OnInit, OnDestroy, UiOnParamsChanged {
  public loading:string;
  public title:ITranslateText;

  public sendMail:boolean = true;
  public showSendMail:boolean = true;

  private savingUserProfileMessage:Noty;
  private previousRegistrationState:string;
  private previousUIData:IUserProfileUIData;
  private previousAction:string;

  public wizardId:string = USERPROFILE_WIZARD;
  private wizard:GaWizard;

  @Input() public userId:string;
  @Input() public companyId?:string;
  @Input() public webshopId?:string;
  @Input() public countryId?:string;
  @Input() public firstName?:string;
  @Input() public genericUserFn?:Function;
  @Input() public showCancelButton?:boolean;
  @Output() public onSaved:EventEmitter<IUserData> = new EventEmitter<IUserData>();
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private authService:AuthService,
    private userProfileService:UserProfileService,
    private userProfileDataService:UserProfileDataService,
    private wizardService:GaWizardService,
    private bodyClassService:BodyClassService,
    private messageService:GaMessageService,
    private userDataService:UserDataService,
    private ga1Service:GA1Service,
    private gaEventService: GaEventService,
    private translationService:TranslationService,
    private stateService:StateService
  ) {
    this.wizard = this.wizardService.getInstance(USERPROFILE_WIZARD);
    this.bodyClassService.add('user-profile');
  }

  public ngOnInit():void {
    this.start(this.userId);
  }

  private start(userId:string):Promise<IUserData> {
    this.userProfileService.setUserProfileId(userId);
    this.loading = 'APP.USER_PROFILE.LOADING';
    this.userProfileService.setWebshopId(this.webshopId);
    this.userProfileService.setCompanyId(this.companyId);
    this.userProfileService.setCountryId(this.countryId);
    if (this.firstName) {
      this.userProfileService.setFirstName(this.firstName);
    }
    this.userProfileService.setGenericUserFn(this.genericUserFn);

    return this.userProfileService.getUserProfilePromise()
      .then((userData:IUserData) => {
        this.title = this.getWizardTitle(userData);
        this.initWizard(userData);
        this.previousRegistrationState = userData.registrationState;
        this.previousUIData = {
          firstname: userData.firstname,
          lastname: userData.lastname,
          email: userData.email,
          gaLayout: userData.gaLayout,
          language: userData.language
        };
        return userData;
      })
      .finally(() => {
        this.loading = undefined;
      });
  }

  private getWizardTitle(userData:IUserData):ITranslateText {
    return {
      translateText: this.userId? 'APP.USER_PROFILE.TITLE' : 'APP.USER_PROFILE.MY_TITLE',
      translateValues: { name: this.userId? `(${userData?.location?.company?.onlySSO ? userData.email : userData.login || (userData.firstname? userData.firstname + ' ' + userData.lastname : 'NEW')})` : '' }
    };
  }

  private doSave(approve:boolean, accept:boolean, create:boolean):void {
    this.savingUserProfileMessage = this.messageService.showStatusMessage(this.translationService.instant('APP.USER_PROFILE.USER_INFO.SAVING_PROFILE'));
    if (accept) {
      this.wizard.setActionEnabled(CREATE_ACTION, false);
      this.wizard.setActionEnabled(SAVE_ACTION, false);
      this.wizard.setActionBusy(APPROVE_ACTION, true);
    } else {
      this.wizard.setActionBusy(SAVE_ACTION, true);
      this.wizard.setActionEnabled(APPROVE_ACTION, false);
    }

    if (create) {
      this.wizard.setActionBusy(CREATE_ACTION, true);
    }

    this.wizard.setActionEnabled(REJECT_ACTION, false);

    const type:string = approve? 'APPROVE' : 'SAVE_PROFILE';
    this.wizard.validateCurrent(type)
      .then(() => this.wizard.validateAll(type))
      .then(() => {
        const email:string = this.userProfileService.getUserInfo().email;
        if (create || email !== this.previousUIData.email) {
          return this.validateEmailSuffix(email);
        }
      })
      .then(() => {
        let userData:IUserData;
        return this.saveProfile(accept, create)
          .then((ud:IUserData) => {
            userData = ud;
            //already update the user profile id (for new users)
            this.userProfileService.updateUserProfileId(ud.id);
          })
          .then(() => {
            // change the token if the user has changed his own password
            if (!this.userProfileService.manageExternalUser()) {
              this.authService.setCurrentUser(userData);
              if (userData.token) {
                this.authService.setToken(userData.token);
              }
            }
          })
          .then(this.userProfileService.saveAndLoadPermissions)
          .then(() => {
            //now set the user data
            this.userProfileService.setUserData(userData);
          })
          .then(() => userData)
          .then(this.afterSave);
      })
      .finally(this.stopSaveBusy);
  }

  private save = ():void => {
    this.previousAction = SAVE_ACTION;
    this.doSave(false, false, false);
  };

  private sendUserProfile = ():void => {
    this.wizard.setActionBusy(SEND_PROFILE_ACTION, true);
    this.wizard.setActionEnabled(SEND_PROFILE_ACTION, false);

    this.userProfileDataService.sendUserProfile(this.userProfileService.getUserProfileId())
      .then(() => {
        this.messageService.showFeedbackMessage('APP.USER_PROFILE.USER_PROFILE_SENT');
      })
      .catch(() => {
        this.wizard.setActionEnabled(SEND_PROFILE_ACTION, true);
      })
      .finally(() => {
        this.wizard.setActionBusy(SEND_PROFILE_ACTION, false);
      });
  };

  private reject = ():void => {
    this.previousAction = REJECT_ACTION;
    this.rejectProfile();
  };

  private approve = ():void => {
    this.previousAction = APPROVE_ACTION;
    this.doSave(true, true, false);
  };

  private create = ():void => {
    this.previousAction = CREATE_ACTION;
    this.doSave(false, true, true);
  };

  private saveProfile = (approve, create):Promise<IUserData> => {
    const userProfileRequest:IUserProfileRequest = this.userProfileService.createUserProfileRequest(approve, false);
    userProfileRequest.mailNotificationToUser = this.sendMail;
    if (create) {
      return this.userProfileDataService.createUserProfile(userProfileRequest);
    } else {
      return this.userProfileDataService.updateUserProfile(this.userProfileService.getUserProfileId(), userProfileRequest);
    }
  };

  private rejectProfile = ():void => {
    const userProfileRequest:IUserProfileRequest = this.userProfileService.createUserProfileRequest(false, true);
    userProfileRequest.userRegistrationApproved = false;
    userProfileRequest.mailNotificationToUser = this.sendMail;

    this.wizard.setActionEnabled(SAVE_ACTION, false);
    this.wizard.setActionEnabled(APPROVE_ACTION, false);
    this.wizard.setActionBusy(REJECT_ACTION, true);

    this.userProfileDataService.updateUserProfile(this.userProfileService.getUserProfileId(), userProfileRequest)
      .then((ud:IUserData) => {
        this.userProfileService.setUserData(ud);
        this.afterSave(ud);
      })
      .finally(() => {
        this.wizard.setActionEnabled(SAVE_ACTION, true);
        this.wizard.setActionEnabled(APPROVE_ACTION, true);
        this.wizard.setActionBusy(REJECT_ACTION, false);
      });

  };

  private afterSave = (userData:IUserData):Promise<IUserData> => {
    this.title = this.getWizardTitle(userData);
    this.messageService.showFeedbackAfterSave(this.translationService.instant('APP.USER_PROFILE.TITLE', {name: ''}));
    const currentRegistrationState:string = userData.registrationState;
    const currentUIData:IUserProfileUIData = {
      firstname: userData.firstname,
      lastname: userData.lastname,
      email: userData.email,
      language: userData.language,
      gaLayout: userData.gaLayout
    };

    let p:Promise<any> = Promise.resolve();

    if (!this.userProfileService.manageExternalUser() && !_.isEqual(this.previousUIData, currentUIData)) {
      console.debug('Reload UI');
      p = this.authService.createNewSession(true);
    }

    return p.then(() => {
      this.initWizard(userData);

      // when user profile is not opened in a dialog
      if (!this.wizard.isInDialog()) {
        // After creation of a new user, jump to the permissions screen
        if (this.userProfileService.manageExternalUser() && this.previousAction === CREATE_ACTION) {
          const state = userData.registrationState === AuthService.STATE_WAITING_APPROVAL? STATE_USER_PERMISSIONS : '.'; // . is the current state
          this.stateService.go(state, { userId: userData.id }, {
            location: 'replace',
            custom: { afterCreate: true }
          });
        }

        //notify the child controllers
        this.notifyUserProfileUpdated(userData);

        // set the new token when the user sets a password after registration
        if (!this.userProfileService.manageExternalUser()) {
          if (this.previousRegistrationState === AuthService.STATE_APPROVED && currentRegistrationState === AuthService.STATE_REGISTERED) {
            this.stateService.go(STATE_HOME_PAGE, { frameless: false });
          }
        }
      }

      this.previousRegistrationState = currentRegistrationState;
      this.previousUIData = currentUIData;
      this.onSaved.emit(userData);
      this.ga1Service.sendMessage(MESSAGE_USERPROFILE_SAVED, userData);
      return userData;
    });

  };

  // check if the email suffix is valid
  private validateEmailSuffix(email:string):Promise<void> {
    const company:ICompany = this.userProfileService.getUserInfo().company;

    return this.userDataService.checkEmailSuffix(company.companyType, company.id, email).then((response:{ validSuffix:boolean }) => {
      return new Promise((resolve, reject) => {
        if (!response.validSuffix) {

          // suspend saving status message when the modal is shown
          this.messageService.closeMessage(this.savingUserProfileMessage);
          const emailPart:string[] = email.split('@');

          const confirmSuffixDialog:IDialog = this.messageService.showDialog({
            component: DefaultDialogComponent,
            wrapperComponent: false,
            inputs: {
              title: 'APP.USER_PROFILE.USER_INFO.CONFIRM_SUFFIX_TITLE',
              message:
                `<p>${this.translationService.instant('APP.USER_PROFILE.USER_INFO.CONFIRM_SUFFIX_MESSAGE')}</p>
                 <div class="email-address">${emailPart[ 0 ]}@<span class="highlight-text">${emailPart[ 1 ]}</span></div>`,
              buttons: [ {
                label: 'GA.GENERAL.YES_SURE',
                className: 'btn btn-success',
                onClick: () => {
                  // resume saving status message
                  this.savingUserProfileMessage = this.messageService.showStatusMessage(this.translationService.instant('APP.USER_PROFILE.USER_INFO.SAVING_PROFILE'));
                  confirmSuffixDialog.hide();
                  resolve();
                }
              }, {
                label: 'GA.GENERAL.CANCEL_SAVE',
                className: 'btn btn-danger',
                onClick: () => {
                  confirmSuffixDialog.hide();
                  reject();
                }
              }  ]
            },
            size: DialogSize.MD
          });
        } else {
          return resolve();
        }
      });
    });
  }

  private stopSaveBusy = ():void => {
    this.wizard.setActionBusy(CREATE_ACTION, false);
    this.wizard.setActionEnabled(CREATE_ACTION, true);
    this.wizard.setActionBusy(SAVE_ACTION, false);
    this.wizard.setActionEnabled(SAVE_ACTION, true);
    this.wizard.setActionBusy(APPROVE_ACTION, false);
    this.wizard.setActionEnabled(APPROVE_ACTION, true);
    this.wizard.setActionEnabled(REJECT_ACTION, true);

    this.messageService.closeMessage(this.savingUserProfileMessage);
  };

  private initWizard(userData:IUserData):void {
    const registrationState:string = this.userProfileService.getUserInfo().registrationState;

    // define wizard steps
    const steps:IWizardStep[] = [
      this.wizardService.createStep(UserProfileStepIds.USER_INFO, 'APP.USER_PROFILE.TABS.USER_INFO', UserInfoComponent, STATE_USER_INFO, this.stateService.params, undefined, true),
      this.wizardService.createStep(UserProfileStepIds.USER_PERMISSIONS, 'APP.USER_PROFILE.TABS.PERMISSIONS', UserPermissionsComponent, STATE_USER_PERMISSIONS, this.stateService.params, undefined, true)
    ];

    // add necessary steps for registered user
    if (registrationState) {
      // add notification step
      steps.push(
        this.wizardService.createStep(UserProfileStepIds.USER_NOTIFICATIONS, 'APP.USER_PROFILE.TABS.NOTIFICATIONS', UserNotificationsComponent, STATE_USER_NOTIFICATIONS, this.stateService.params, undefined, true, undefined)
      );
      // add user training tab for all users except customer
      if (!this.authService.checkCustomersUser()) {
        steps.push(
          this.wizardService.createStep(UserProfileStepIds.USER_TRAINING, 'APP.USER_PROFILE.TABS.TRAINING', UserTrainingComponent, STATE_USER_TRAINING, this.stateService.params, undefined, true, undefined)
        );
      }
    }

    if (this.authService.checkAcslUser() && this.userProfileService.getUserProfileId() && this.userProfileService.manageExternalUser()) {
      steps.push(
        this.wizardService.createStep(UserProfileStepIds.USER_AUDIT, 'APP.USER_PROFILE.TABS.AUDIT', UserAuditComponent, STATE_USER_AUDIT, this.stateService.params, undefined, true, undefined)
      );
    }

    this.wizard.steps(steps);
    this.wizard.sync();

    this.showSendMail = false;

    const actions:IWizardButton[] = [];
    if (!registrationState) { //new user
      this.showSendMail = true;

      actions.push(this.wizardService.createActionButton(CREATE_ACTION,
        this.translationService.instant('APP.USER_PROFILE.CREATE_PROFILE'),
        'btn btn-success',
        this.create
      ));
    } else if (userData.userCategory !== 'REAL') {
      // no actions whatsoever
    } else if (registrationState === AuthService.STATE_WAITING_APPROVAL) {
      this.showSendMail = true;

      actions.push(this.wizardService.createActionButton(
        SAVE_ACTION,
        this.translationService.instant('APP.USER_PROFILE.SAVE_PROFILE'),
        'btn btn-primary',
        this.save
      ));

      actions.push(this.wizardService.createActionButton(
        APPROVE_ACTION,
        this.translationService.instant('GA.BUTTONS.APPROVE'),
        'btn btn-success',
        this.approve,
        false,
        false,
        'fa-check-circle',
        userData.autoApproval
      ));

      actions.push(this.wizardService.createActionButton(
        REJECT_ACTION,
        this.translationService.instant('GA.BUTTONS.REJECT'),
        'btn btn-danger',
        this.reject
      ));

    } else if (registrationState === AuthService.STATE_REJECTED) {
      // no action buttons
    } else {

      if (this.userProfileService.manageExternalUser() && [
        AuthService.STATE_APPROVED,
        AuthService.STATE_REGISTERED
      ].indexOf(registrationState) !== -1) {
        actions.push(this.wizardService.createActionButton(
          SEND_PROFILE_ACTION,
          this.translationService.instant('GA.BUTTONS.SEND_PROFILE'),
          'btn btn-primary',
          this.sendUserProfile,
          undefined,
          true
        ));
      }

      actions.push(
        this.wizardService.createActionButton(
          SAVE_ACTION,
          this.translationService.instant('APP.USER_PROFILE.SAVE_PROFILE'),
          'btn btn-success',
          this.save
        )
      );
    }

    if (this.showCancelButton) {
      actions.push(
        this.wizardService.createActionButton(
          CANCEL_ACTION,
          this.translationService.instant('GA.BUTTONS.CANCEL'),
          'btn btn-danger',
          () => {
            this.onCancel.emit();
          }
        )
      );
    }

    this.wizard.actions(actions);
  }

  private notifyUserProfileUpdated = (userData:IUserData):void => {
    this.gaEventService.sendMessage(EVENT_USER_PROFILE_UPDATED, userData);
  };

  public ngOnDestroy():void {
    this.bodyClassService.remove('user-profile');
    this.clearCaches();
  }

  private clearCaches() {
    this.wizard.clear();
    this.userProfileService.clear();
    this.userProfileDataService.clear();
  }

  public onMailNotificationChange(sendMail:boolean):void {
    this.sendMail = sendMail;
  }

  public uiOnParamsChanged = (newValues:any, transition:Transition):void => {
    const fromUserId:string = transition.params('from').userId;
    const toUserId:string = this.stateService.params.userId;
    console.log(`uiOnParamsChanged: ${fromUserId} -> ${toUserId}`);
    if (!transition.options().custom.afterCreate) {
      //restart controller
      this.clearCaches();
      this.start(toUserId)
        .then(this.notifyUserProfileUpdated);
    }
  };
}